import type { BoardFeed } from "../boardFeeds";
import type { DeviceCategory } from "../deviceCategories";
import type { CableSourceEdit } from "../CablesChatPanel/types";
import type { GapTranslate } from "../findCableGaps";
import type {
  KarlAnswer,
  KarlAsk,
  KarlAskGroup,
  KarlAskNote,
  KarlAskTool,
} from "../karlAsks";
import type { KarlStepsResult } from "../karlSteps";
import type { HeightTarget } from "../installationHeights";

/**
 * A plan interaction an answer arms. The estimator is about to click the
 * drawing, so the ask stays on screen until the click lands or he backs out —
 * moving the question out from under him is how he loses the thread.
 */
export type KarlAskArm =
  | { kind: "moveSource"; label: string }
  | { kind: "placeSource"; name: string }
  | { kind: "drawBox"; wayKey?: string; circuitId?: string }
  | { kind: "boxAssign"; circuitId: string }
  | { kind: "boxRemove"; circuitId: string }
  /** Waiting for him to click the run a removal group belongs on. */
  | { kind: "pickRoute"; groupId: string }
  /** Waiting for him to close a zone for this board on the plan. */
  | { kind: "drawZone"; label: string }
  /** Waiting for him to click the main board, or bare plan for an incoming feed. */
  | { kind: "pickMainBoard"; placesAs: "board" | "feedIn" }
  /** Waiting for him to click the board that feeds a category. */
  | { kind: "pickFeederFor"; category: DeviceCategory }
  /** Waiting for him to click the board that feeds this one. */
  | { kind: "pickUpstreamFor"; toLabel: string };

/** How an ask left the queue. `committed` only ever follows an arm. */
export type KarlAskOutcome = "accepted" | "declined" | "committed";

/** Undoes a settled answer when the estimator goes back and answers again. */
export type KarlAskRevert =
  | { kind: "unconfirm"; key: string }
  | { kind: "restoreSource"; label: string }
  | { kind: "removeSource"; label: string }
  | { kind: "clearMainBoard" }
  | { kind: "clearBoardRole"; label: string }
  | { kind: "clearBoardFeeds"; label: string }
  | { kind: "billCategory"; category: DeviceCategory }
  | { kind: "clearHeight"; target: HeightTarget }
  | { kind: "clearFeederFrom"; toLabel: string }
  | { kind: "feederOnFloor"; toLabel: string }
  | { kind: "clearFeederType"; toLabel: string };

export interface KarlAskRecord {
  askId: string;
  answerId: string;
  outcome: KarlAskOutcome;
  /**
   * The roster key — `KarlAsk.subjectKey`, never the display board name. The
   * roster matches `confirmed` on its own key, so confirming by name silently
   * settles nothing.
   */
  confirmKey?: string;
  revert?: KarlAskRevert;
}

export interface KarlAskArmed {
  askId: string;
  answerId: string;
  arm: KarlAskArm;
  /**
   * True when a TOOL armed this, not an answer. Committing a tool performs its
   * work and hands the same question straight back — it must never push a
   * history record, or using a tool would answer the question for him.
   */
  isTool?: boolean;
}

export interface KarlAskQueueState {
  /** Settled asks in the order they were settled. Back walks this. */
  history: KarlAskRecord[];
  /** Ask ids put off by "Later", in defer order. Never also in `history`. */
  deferred: string[];
  /** At most one. While set, the ask stays on screen in its armed state. */
  armed: KarlAskArmed | null;
  /** Steps back from the head; 0 is live. There is no separate index. */
  rewind: number;
}

const NO_RECORDS: KarlAskRecord[] = [];
const NO_ASK_IDS: string[] = [];
Object.freeze(NO_RECORDS);
Object.freeze(NO_ASK_IDS);

// Frozen because `readKarlAskQueueState` hands this very object back on every
// miss: one consumer pushing into `history` would corrupt every later read.
export const emptyKarlAskQueueState: KarlAskQueueState = Object.freeze({
  history: NO_RECORDS,
  deferred: NO_ASK_IDS,
  armed: null,
  rewind: 0,
});

export type KarlAskQueueMode = "asking" | "armed" | "revisiting" | "finished";

export interface KarlAskQueueView {
  ask: KarlAsk | null;
  mode: KarlAskQueueMode;
  /** Set only while `mode` is `armed`, so the card can say what it waits for. */
  arm: KarlAskArm | null;
  /** Asks settled behind him. The progress bar is this over `total`. */
  behind: number;
  /** Asks still in front of him, not counting the one on screen. */
  ahead: number;
  /** `behind` + everything still queued. Does not shrink as he answers. */
  total: number;
  /** 1-based place of the ask on screen; 0 when the queue is finished. */
  position: number;
  /** 1-based place of the ask's subject (its board) among the group's subjects in walk order; 0 without one. */
  subjectPosition: number;
  /** Distinct subjects in the group. Unlike `total`, a follow-up about the same board does not move it. */
  subjectTotal: number;
  /** Never counts the ask on screen: the first question reads 0. */
  progress: number;
  canGoBack: boolean;
  canGoForward: boolean;
  /** True when the ask on screen is one he put off earlier. */
  isDeferred: boolean;
  /**
   * True when the armed thing was a TOOL, not an answer. A tool leaves the
   * question open by design, so the card must keep the ladder up: hiding it
   * meant he had to answer before he could finish working on the run.
   */
  isToolArm: boolean;
}

export interface OrderKarlAsksArgs {
  asks: KarlAsk[];
  state: KarlAskQueueState;
}

export interface OrderKarlAsksResult {
  /** Never answered, never deferred — in build order. */
  pending: KarlAsk[];
  /** Put off by "Later", in defer order, and still present in `asks`. */
  deferred: KarlAsk[];
  /** `pending` then `deferred`: what he still has to work through. */
  queue: KarlAsk[];
  /** Never answered in this walk, but the plan already holds his answer: counted done, not asked. */
  stored: KarlAsk[];
}

export type OrderKarlAsks = (args: OrderKarlAsksArgs) => OrderKarlAsksResult;

export interface ReadAskQueueArgs {
  asks: KarlAsk[];
  state: KarlAskQueueState;
}

export type ReadAskQueue = (args: ReadAskQueueArgs) => KarlAskQueueView;

/**
 * What the queue asks the app to do. The reducer stays pure and names the
 * effect; the hook is the only thing that touches `sourceEdit`.
 */
export type KarlAskEffect =
  | { kind: "arm"; arm: KarlAskArm }
  | { kind: "cancelArm" }
  | { kind: "confirm"; key: string }
  | { kind: "unconfirm"; key: string }
  | { kind: "removeSource"; label: string }
  | { kind: "removeSourceTo"; label: string; toLabel: string }
  | { kind: "restoreSource"; label: string }
  | { kind: "renameSource"; label: string }
  /** Put the devices the box caught onto this run. */
  | { kind: "assignSelection"; circuitId: string }
  /** Take the devices the box caught out of the takeoff. */
  | { kind: "removeSelection" }
  /** Put a whole removal group onto the run he just picked, and recount. */
  | { kind: "assignRemovalGroup"; groupId: string }
  /** Put a whole removal group back where it came from. */
  | { kind: "restoreRemovalGroup"; groupId: string }
  /** Seal a removal group: it has been asked about and answered. */
  | { kind: "closeRemovalGroup"; groupId: string }
  /** Take named devices out of the takeoff, under a group id chosen up front. */
  | { kind: "parkDevices"; groupId: string; destinationIds: string[] }
  /** Open a run's shape for editing on the plan. */
  | { kind: "editRouteShape"; circuitId: string }
  | { kind: "setMainBoard"; label: string }
  | { kind: "clearMainBoard" }
  | { kind: "setBoardRole"; label: string; role: "feeds" | "viaMain" }
  | { kind: "clearBoardRole"; label: string }
  | { kind: "setBoardFeeds"; label: string; feed: BoardFeed }
  | { kind: "clearBoardFeeds"; label: string }
  | { kind: "unbillCategory"; category: DeviceCategory }
  | { kind: "billCategory"; category: DeviceCategory }
  | { kind: "setHeight"; target: HeightTarget; heightM: number }
  | { kind: "clearHeight"; target: HeightTarget }
  | { kind: "setFeederFrom"; toLabel: string; fromLabel: string }
  | { kind: "clearFeederFrom"; toLabel: string }
  | { kind: "setFeederOffFloor"; toLabel: string }
  | { kind: "feederOnFloor"; toLabel: string }
  | { kind: "setFeederType"; toLabel: string; cableType: string }
  | { kind: "clearFeederType"; toLabel: string };

export type KarlAskQueueEvent =
  | { type: "answer"; ask: KarlAsk; answer: KarlAnswer }
  /** He reached for a tool. Arms its work; the question stays unanswered. */
  | { type: "tool"; ask: KarlAsk; tool: KarlAskTool }
  | { type: "commit" }
  | { type: "cancel" }
  | { type: "back" }
  | { type: "forward" }
  /** Back to the first answered ask, keeping every answer. */
  | { type: "rewindAll" }
  /**
   * Put the walk on ONE ask — the run he asked about. Settled, it is in
   * `history` and this is a rewind to it; still pending, everything ahead of it
   * is deferred, which is the back of the queue and not the bin. There is no
   * index to move: an index over a list that rebuilds itself is what froze the
   * progress bar and made the counter read "1 of 43, 1 of 42, 1 of 41".
   */
  | { type: "jumpTo"; askId: string }
  /** Ids the rebuild no longer carries; their history records survive. */
  | { type: "vanished"; askIds: string[] };

export interface KarlAskQueueReducerArgs {
  state: KarlAskQueueState;
  event: KarlAskQueueEvent;
  asks: KarlAsk[];
}

export interface KarlAskQueueReducerResult {
  state: KarlAskQueueState;
  /** In the order they must run. Empty when nothing outside the queue changes. */
  effects: KarlAskEffect[];
}

export type KarlAskQueueReducer = (
  args: KarlAskQueueReducerArgs,
) => KarlAskQueueReducerResult;

/**
 * What one answer means, before any state moves. Splitting this out keeps the
 * reducer free of the answer vocabulary and makes every mapping testable.
 */
export interface AskAnswerPlan {
  /** Set when the answer hands the estimator to the drawing. */
  arm: KarlAskArm | null;
  outcome: KarlAskOutcome;
  confirmKey?: string;
  revert?: KarlAskRevert;
  effects: KarlAskEffect[];
  /** True for "Later": it goes to the back of the queue, it is not settled. */
  isDeferral: boolean;
}

export interface AskAnswerEffectArgs {
  ask: KarlAsk;
  answer: KarlAnswer;
}

export type AskAnswerEffect = (args: AskAnswerEffectArgs) => AskAnswerPlan;

/**
 * The plan-side facts an arm is settled against. Every field is already wired
 * through `CableSourceEdit`; this is a snapshot of them at one render.
 */
export interface KarlPlanSnapshot {
  placed: Record<string, unknown>;
  added: Record<string, { name: string }>;
  removed: Record<string, unknown>;
  grabbedLabel: string | null;
  addingName: string | null;
  isSelectingDevices: boolean;
  /**
   * `deviceSelection !== null` — NOT whether the box caught anything. The plan
   * publishes a selection even for an empty catch, so counting devices instead
   * would make a box that caught nothing identical to backing out, and the ask
   * would report cancelled when the estimator actually drew.
   */
  hasDeviceSelection: boolean;
  /** The run isolated on the plan — how "click the one they belong on" lands. */
  selectedCircuit: string | null;
  /** Every zone on the plan; the writer replaces the list on every change. */
  zones: Array<{ sourceLabel: string }>;
  drawingZoneFor: string | null;
  /** The main board he answered, by marker label; a suggestion is not an answer. */
  mainBoardLabel: string | null;
  pickingMainBoard: boolean;
  /** The category he is showing a feeder for, while he is. */
  pickingFeederFor: DeviceCategory | null;
  /** His stored feed answers; a new object on every change. */
  boardFeeds: unknown;
  /** The board he is showing an upstream for, while he is. */
  pickingUpstreamFor: string | null;
  /** The feeders as they stand; a new object when he names an upstream. */
  feeders: unknown;
}

export type KarlArmSettlement = "committed" | "cancelled" | "pending";

export interface AskArmSettledArgs {
  arm: KarlAskArm;
  before: KarlPlanSnapshot;
  after: KarlPlanSnapshot;
}

export type AskArmSettled = (args: AskArmSettledArgs) => KarlArmSettlement;

export interface UseKarlAskQueueArgs {
  steps: KarlStepsResult;
  sourceEdit?: CableSourceEdit;
  /** One group at a time: the wizard opens the sources before the grouping. */
  group: KarlAskGroup;
  projectId: string | null | undefined;
  planName: string | null | undefined;
  t: GapTranslate;
}

export interface UseKarlAskQueueResult {
  view: KarlAskQueueView;
  /** Facts nobody can act on here. Never asks. */
  notes: KarlAskNote[];
  answer: (ask: KarlAsk, answer: KarlAnswer) => void;
  back: () => void;
  forward: () => void;
  /** Reopens a finished group at its first ask, keeping every answer. */
  walkAgain: () => void;
  /**
   * Puts the walk on one ask, so "ask Karl about this run" lands on the card he
   * works in rather than a paragraph about it. False when this group carries no
   * such ask, so the caller can do something else instead of jumping nowhere.
   */
  jumpTo: (askId: string) => boolean;
  cancelArm: () => void;
  /** Runs a tool against the ask on screen without answering it. */
  runTool: (ask: KarlAsk, tool: KarlAskTool) => void;
  /**
   * Forgets every answer for this group and clears what was stored. The only
   * way back from a queue that believes it is finished when it is not.
   */
  reset: () => void;
  /** True once this group has nothing left to ask and nothing armed. */
  isFinished: boolean;
  /**
   * What has been settled, for surfaces outside the queue — the circuit list
   * marks a route done from this. One derivation, so the list can never claim
   * work the queue does not have.
   */
  settled: Array<{ askId: string; outcome: KarlAskOutcome }>;
}

export type UseKarlAskQueue = (
  args: UseKarlAskQueueArgs,
) => UseKarlAskQueueResult;
